import { useEffect, useRef, useState } from "react";
import { pickRecorderMimeType } from "../../capture/video";
import { LivePoseDetector, frameBrightness } from "../../capture/livePose";
import { evaluateCapture, type CoachVerdict } from "../../capture/coach";
import { drawSkeleton } from "../../viz/overlay";
import type { GaitView } from "../../store/types";

interface Props {
  view: GaitView;
  onRecorded: (blob: Blob) => void;
  onCancel: () => void;
  /**
   * Seconds to count down before recording actually starts, so the person
   * has time to position themselves. Undefined/0 = start immediately
   * (unchanged gait behavior).
   */
  countdownSec?: number;
  /**
   * Auto-stop recording after this many seconds. Undefined = no limit
   * (unchanged gait behavior — gait walks need a variable-length clip).
   */
  maxDurationSec?: number;
  /** Short instruction shown as an overlay (and optionally spoken) once recording begins. */
  instructionText?: string;
  /** Positioning hint spoken once when this step is shown (before recording starts). */
  setupText?: string;
  /** Draw a live skeleton overlay on the video during preview + recording (eGym-style). */
  showSkeleton?: boolean;
  /**
   * Speak the countdown, the instruction, and "Aufnahme fertig" via the
   * Web Speech API. Best-effort — silently no-ops where unsupported.
   */
  speak?: boolean;
}

interface ZoomCaps { min: number; max: number; step: number; }

function speakText(text: string): void {
  try {
    if (!("speechSynthesis" in window)) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "de-DE";
    u.rate = 1.02;
    window.speechSynthesis.cancel(); // don't queue/overlap over a stale utterance
    window.speechSynthesis.speak(u);
  } catch {
    /* best-effort only */
  }
}

/**
 * In-browser webcam recorder with lens (camera) selection and hardware zoom.
 * On iPhone the native capture button is usually the better path (it exposes
 * the 0.5× ultra-wide and pinch-zoom directly); this recorder is for desktop /
 * PWA use and devices that expose zoom via the MediaStream track.
 */
export function WebcamRecorder({
  view,
  onRecorded,
  onCancel,
  countdownSec,
  maxDurationSec,
  instructionText,
  setupText,
  showSkeleton,
  speak,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number>(0);
  const detectorRef = useRef<LivePoseDetector | null>(null);
  const coachCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const coachTimerRef = useRef<number>(0);
  const countdownTimerRef = useRef<number>(0);

  const [recording, setRecording] = useState(false);
  const [verdict, setVerdict] = useState<CoachVerdict | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string>("");
  const [zoomCaps, setZoomCaps] = useState<ZoomCaps | null>(null);
  const [zoom, setZoom] = useState(1);
  const [torch, setTorch] = useState(false);
  const [torchAvail, setTorchAvail] = useState(false);
  const [countdownValue, setCountdownValue] = useState<number | null>(null);
  const [justFinished, setJustFinished] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await startStream();
      if (cancelled) return;
      try {
        const list = await navigator.mediaDevices.enumerateDevices();
        setDevices(list.filter((d) => d.kind === "videoinput"));
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
      stopTimer();
      window.clearInterval(countdownTimerRef.current);
      try {
        window.speechSynthesis?.cancel();
      } catch {
        /* ignore */
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Speak the positioning hint once per step, shortly after the camera
  // preview has had a moment to appear (avoids talking over the browser's
  // own camera-permission prompt on the very first mount).
  useEffect(() => {
    if (!speak || !setupText) return;
    const t = window.setTimeout(() => speakText(setupText), 600);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupText]);

  // Capture coach: load a live pose detector once, evaluate framing periodically.
  useEffect(() => {
    const detector = new LivePoseDetector();
    detectorRef.current = detector;
    coachCanvasRef.current = document.createElement("canvas");
    detector.init();
    return () => {
      detector.close();
      detectorRef.current = null;
    };
  }, []);

  // Readiness verdict only matters before recording starts. The live
  // skeleton overlay, if enabled, keeps drawing during recording too
  // (eGym-style self-view) — it just stops feeding the (now irrelevant)
  // framing verdict once recording is underway.
  useEffect(() => {
    coachTimerRef.current = window.setInterval(() => {
      const video = videoRef.current;
      const det = detectorRef.current;
      const canvas = coachCanvasRef.current;
      const overlay = overlayCanvasRef.current;
      if (!video || !det || !det.isReady()) return;
      const frame = det.detect(video, performance.now());

      if (!recording && canvas) {
        const brightness = frameBrightness(video, canvas);
        setVerdict(evaluateCapture({ frame, view, brightness }));
      }

      if (showSkeleton && overlay && video.videoWidth) {
        if (overlay.width !== video.videoWidth) overlay.width = video.videoWidth;
        if (overlay.height !== video.videoHeight) overlay.height = video.videoHeight;
        const ctx = overlay.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, overlay.width, overlay.height);
          if (frame) drawSkeleton(ctx, frame, overlay.width, overlay.height);
        }
      }
    }, 280);
    return () => window.clearInterval(coachTimerRef.current);
  }, [recording, view, showSkeleton]);

  async function startStream(id?: string) {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: id
          ? { deviceId: { exact: id }, width: { ideal: 1280 }, height: { ideal: 720 } }
          : { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      readCapabilities(stream);
      setError(null);
    } catch {
      setError("Kamera nicht verfügbar — Zugriff erlauben, oder Video importieren.");
    }
  }

  function readCapabilities(stream: MediaStream) {
    const track = stream.getVideoTracks()[0];
    const caps = (track?.getCapabilities?.() ?? {}) as MediaTrackCapabilities & {
      zoom?: { min: number; max: number; step: number };
      torch?: boolean;
    };
    if (caps.zoom && typeof caps.zoom.max === "number") {
      setZoomCaps({ min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step || 0.1 });
      setZoom(caps.zoom.min || 1);
    } else {
      setZoomCaps(null);
    }
    setTorchAvail(Boolean(caps.torch));
    setTorch(false);
  }

  async function applyZoom(v: number) {
    setZoom(v);
    const track = streamRef.current?.getVideoTracks()[0];
    try {
      await track?.applyConstraints({ advanced: [{ zoom: v }] } as unknown as MediaTrackConstraints);
    } catch {
      /* unsupported */
    }
  }

  async function toggleTorch() {
    const next = !torch;
    setTorch(next);
    const track = streamRef.current?.getVideoTracks()[0];
    try {
      await track?.applyConstraints({ advanced: [{ torch: next }] } as unknown as MediaTrackConstraints);
    } catch {
      /* unsupported */
    }
  }

  function startTimer() {
    setElapsed(0);
    timerRef.current = window.setInterval(() => {
      setElapsed((e) => {
        const next = e + 1;
        if (maxDurationSec && next >= maxDurationSec) {
          // Auto-stop once the clip hits its max length — fire on the next
          // tick so `elapsed` has already committed to its final value.
          window.setTimeout(() => finishRecording(), 0);
        }
        return next;
      });
    }, 1000);
  }
  function stopTimer() {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = 0;
  }

  /** Actually starts MediaRecorder. Called either immediately (no countdown
   *  configured) or once a configured countdown reaches zero. */
  function beginRecording() {
    const stream = streamRef.current;
    if (!stream) return;
    chunksRef.current = [];
    const mimeType = pickRecorderMimeType();
    const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    rec.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType ?? "video/webm" });
      streamRef.current?.getTracks().forEach((t) => t.stop());
      onRecorded(blob);
    };
    recorderRef.current = rec;
    rec.start();
    setRecording(true);
    setJustFinished(false);
    startTimer();
    if (instructionText) {
      if (speak) speakText(instructionText);
    }
  }

  /** Public "Aufnahme starten" handler — runs the countdown first if configured. */
  function start() {
    if (!countdownSec || countdownSec <= 0) {
      beginRecording();
      return;
    }
    setCountdownValue(countdownSec);
    if (speak) speakText(String(countdownSec));
    countdownTimerRef.current = window.setInterval(() => {
      setCountdownValue((v) => {
        if (v === null) return null;
        const next = v - 1;
        if (next <= 0) {
          window.clearInterval(countdownTimerRef.current);
          setCountdownValue(null);
          beginRecording();
          return null;
        }
        if (speak) speakText(String(next));
        return next;
      });
    }, 1000);
  }

  /** Stops recording (manual button or auto-stop at maxDurationSec), with a
   *  brief "Aufnahme fertig" confirmation before handing off to onRecorded. */
  function finishRecording() {
    if (!recording) return;
    stopTimer();
    setRecording(false);
    setJustFinished(true);
    if (speak) speakText("Aufnahme fertig.");
    window.setTimeout(() => recorderRef.current?.stop(), 500);
  }

  function stop() {
    finishRecording();
  }

  function lensLabel(d: MediaDeviceInfo, i: number): string {
    const l = d.label || `Camera ${i + 1}`;
    return l.replace(/\s*\(.*?\)\s*/g, "").trim() || l;
  }

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <strong>Capture</strong>
        <span className="muted">
          {justFinished
            ? "✓ Aufnahme fertig"
            : countdownValue !== null
              ? "bereit machen…"
              : recording
                ? `● ${elapsed}s${maxDurationSec ? ` / ${maxDurationSec}s` : ""}`
                : "bereit"}
        </span>
      </div>

      {!recording && countdownValue === null && verdict && (
        <div className={`coach ${verdict.ready ? "ready" : "wait"}`}>
          <div className="coach-head">
            <span className="coach-dot" />
            {verdict.ready ? "Bereit — Aufnahme starten" : verdict.hint}
          </div>
          <div className="coach-checks">
            {verdict.checks.map((c) => (
              <span key={c.key} className={`coach-chip ${c.ok ? "ok" : c.blocking ? "bad" : "warn"}`}>
                {c.ok ? "✓" : "•"} {c.label}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="video-wrap" style={{ marginTop: 12, position: "relative" }}>
        <video ref={videoRef} muted playsInline />
        {showSkeleton && (
          <canvas
            ref={overlayCanvasRef}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
            }}
          />
        )}
        {countdownValue !== null && (
          <div className="capture-countdown" aria-live="polite">
            {countdownValue}
          </div>
        )}
        {recording && instructionText && !justFinished && (
          <div className="capture-instruction">{instructionText}</div>
        )}
        {justFinished && (
          <div className="capture-done" aria-live="polite">
            ✓ Aufnahme fertig
          </div>
        )}
      </div>

      {error ? (
        <p style={{ color: "var(--danger)" }}>{error}</p>
      ) : (
        <>
          {devices.length > 1 && (
            <>
              <label>Kamera / Linse</label>
              <select
                value={deviceId}
                onChange={(e) => {
                  setDeviceId(e.target.value);
                  startStream(e.target.value);
                }}
                disabled={recording}
              >
                <option value="">Standard (hinten)</option>
                {devices.map((d, i) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {lensLabel(d, i)}
                  </option>
                ))}
              </select>
            </>
          )}

          {zoomCaps && (
            <>
              <label>Zoom · {zoom.toFixed(1)}×</label>
              <input
                type="range"
                min={zoomCaps.min}
                max={zoomCaps.max}
                step={zoomCaps.step}
                value={zoom}
                onChange={(e) => applyZoom(parseFloat(e.target.value))}
                style={{ width: "100%" }}
              />
            </>
          )}

          <p className="muted" style={{ marginTop: 10 }}>
            Ganzer Körper im Bild, 5–10 m gehen lassen.
            {!zoomCaps && " Auf dem iPhone gibt der native Kamera-Button 0,5× Weitwinkel und Pinch-Zoom."}
            {torchAvail && (
              <button
                className="btn secondary"
                style={{ width: "auto", padding: "4px 10px", marginLeft: 8 }}
                onClick={toggleTorch}
              >
                {torch ? "Licht aus" : "Licht an"}
              </button>
            )}
          </p>
        </>
      )}

      <div className="row" style={{ gap: 10, marginTop: 12 }}>
        {!recording ? (
          <button
            className={`btn ${verdict?.ready ? "pulse" : ""}`}
            onClick={start}
            disabled={!!error || countdownValue !== null}
          >
            {countdownValue !== null ? `Startet in ${countdownValue}…` : "Aufnahme starten"}
          </button>
        ) : (
          <button className="btn danger" onClick={stop} disabled={justFinished}>
            {justFinished ? "✓ Fertig…" : "Stopp & analysieren"}
          </button>
        )}
        <button
          className="btn secondary"
          onClick={onCancel}
          disabled={recording || countdownValue !== null}
        >
          Abbrechen
        </button>
      </div>
    </div>
  );
}
